<template>
    <modal
        :id="modalId"
        title="Send Message"
        scrollable
        @show="onShow"
        @shown="setData"
        :static="true"
        class="send-email-modal"
    >
        <busy-modal :show="busy"></busy-modal>
        <validation-observer ref="form" v-slot="{ meta, setFieldValue, errors }">
            {{ setInvalid(!meta.valid) }}
            <b-form @submit.prevent="">
                <b-row>
                    <b-col>
                        <validation-provider name="to" rules="required|email">
                            <form-input
                                ref="toInput"
                                floatingLabel="To"
                                v-model="to"
                                :rightButton="{
                                    title: 'CC',
                                    text: 'CC',
                                    variant: 'outline-primary',
                                    toggle: true,
                                }"
                                @handle-right-button-click="toggleCC"
                                :rightButton2="{
                                    title: 'BCC',
                                    text: 'BCC',
                                    variant: 'outline-primary',
                                    toggle: true,
                                }"
                                @handle-right-button-2-click="toggleBCC"
                                :error="errors.to"
                                @input="(val) => setFieldValue('to', val)"
                            />
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-if="isCC" class="no-gutters">
                    <b-col md="12">
                        <validation-provider name="cc" rules="commaSeparatedEmail">
                            <form-input
                                floatingLabel="CC"
                                v-model="cc"
                                class="ml-0 cc-email-input"
                                :error="errors.cc"
                                @input="(val) => setFieldValue('cc', val)"
                            />
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-if="isBCC">
                    <b-col>
                        <validation-provider name="bcc" rules="commaSeparatedEmail">
                            <form-input
                                floatingLabel="BCC"
                                v-model="bcc"
                                :error="errors.bcc"
                                @input="(val) => setFieldValue('bcc', val)"
                            />
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <validation-provider name="subject" rules="required">
                            <form-input
                                floatingLabel="Subject"
                                v-model="subject"
                                :error="errors.subject"
                                @input="(val) => setFieldValue('subject', val)"
                            />
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row class="no-gutters">
                    <b-col md="9">
                        <form-select
                            :options="emailResponseOptions"
                            v-model="selectedEmailResponseOption"
                            floatingLabel="Email Response"
                            placeholder="Insert email template response"
                        />
                    </b-col>
                    <b-col md="3" class="pl-2">
                        <b-button
                            class="apply-response-button"
                            variant="primary"
                            @click="applyResponse"
                            :disabled="isNil(selectedEmailResponseOption)"
                            >Apply</b-button
                        >
                    </b-col>
                </b-row>
                <form-checkbox class="mt-2" v-model="publishInNotice"
                    >Send Push Notification to Subscriber</form-checkbox
                >
                <validation-provider name="message" rules="required">
                    <lx-editor
                        v-model="message"
                        :error="errors.message"
                        @input="(val) => setFieldValue('message', val)"
                    ></lx-editor>
                </validation-provider>
            </b-form>
        </validation-observer>

        <template #modal-footer="{ ok, cancel }">
            <div class="w-100">
                <b-button variant="outline-primary" class="float-left" @click="clearMessage()"> Clear </b-button>
                <b-button variant="primary" class="float-right ml-2" :disabled="invalid" @click="sendEmail()">
                    Send
                </b-button>
                <b-button variant="secondary" class="float-right" @click="cancel()"> Cancel </b-button>
            </div>
        </template>
    </modal>
</template>

<script>
import DOMPurify from 'dompurify';
import isNil from 'lodash/isNil';
import API from '../../../api';
import { mapGetters } from 'vuex';
import { ADMIN_DAMAGE_CLAIMS_EMAIL_ENDPOINT } from '../../../endpoints';
import FormInput from '../../generic/form-input/form-input.vue';
import FormFile from '../../generic/form-file/form-file.vue';
import FormSelect from '../../generic/form-select/form-select.vue';
import FormCheckbox from '../../generic/form-checkbox/form-checkbox.vue';
import FormTextarea from '../../generic/form-textarea/form-textarea.vue';
import LxEditor from '../../generic/lx-editor/lx-editor.vue';
import Modal from '../../generic/modal/modal.vue';
import BusyModal from '../../generic/busy-modal/busy-modal.vue';
import { formatResponseError } from '../../../utils/api';

export default {
    name: 'DamageProtectionSendEmailModal',
    components: {
        Modal,
        FormInput,
        FormSelect,
        FormTextarea,
        FormFile,
        LxEditor,
        BusyModal,
        FormCheckbox,
    },
    props: {
        initialValues: {
            type: Object,
            default: () => ({}),
        },
        modalId: String,
    },
    data() {
        return {
            editorModules: {
                modules: {
                    toolbar: {
                        container: [
                            ['bold', 'italic', 'underline', 'strike'],
                            ['blockquote', 'code-block'],
                            [{ header: 1 }, { header: 2 }],
                            ['clean'],
                            [{ list: 'ordered' }, { list: 'bullet' }],
                        ],
                    },
                    htmlEditButton: {},
                },
            },
            emailResponseOptions: [],
            message: null,
            subject: '',
            selectedEmailResponseOption: null,
            selectedDocumentOption: null,
            selectedCC: null,
            to: null,
            cc: null,
            bcc: null,
            isBCC: false,
            isCC: false,
            ccMode: 'OTHER',
            invalid: false,
            busy: false,
            claimData: null,
            publishInNotice: false,
        };
    },
    created() {
        this.claimData = this.initialValues;
    },
    computed: {
        ...mapGetters('header', ['userID', 'userCompany', 'userEmailSignature']),
    },
    methods: {
        isNil,
        async applyResponse() {
            try {
                const { data } = await API.get(`/system/email-responses/ajax/${this.selectedEmailResponseOption}/0`);
                this.message = data;
            } catch (e) {
                this.errorToast({ text: 'Could not apply responses' });
            }
        },
        cleanMessage(text) {
            const config = {
                ADD_ATTR: ['href', 'name', 'target', 'src', 'style', 'colspan', 'align'],
            };
            return DOMPurify.sanitize(text || '', config);
        },
        async fetchEmailResponses() {
            try {
                const { data } = await API.get(`${ADMIN_DAMAGE_CLAIMS_EMAIL_ENDPOINT()}responses/`);
                this.emailResponseOptions = data.map((item) => ({
                    value: item.id,
                    text: item.title,
                }));
                this.subject = `Lodgix Damage Reimbursement Request #${this.claimData.claim.number}`;
                this.$refs.form.setFieldValue('subject', this.subject);
                this.selectedEmailResponseOption = data.length && data[0].id;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch responses' });
            }
        },
        async onShow() {
            this.$nextTick(() => {
                this.isBCC = false;
                this.isCC = false;
                this.fetchEmailResponses();
                this.to = this.claimData && this.claimData.subscriber_email;
            });
        },
        setData() {
            this.$refs.form.setValues({
                to: this.to,
                cc: this.cc,
                bcc: this.bcc,
                subject: this.subject,
                message: this.message,
            });
        },
        async sendEmail() {
            try {
                const trimEmails = (value) => {
                    return value
                        ? value
                              .split(',')
                              .map((item) => item.trim())
                              .join(',')
                        : null;
                };
                const body = {
                    subject: this.subject,
                    body: this.message,
                    subscriber_pk: this.claimData.subscriber_id,
                    attach_notice: this.publishInNotice,
                    claim_pk: this.claimData.claim.id,
                    cc: trimEmails(this.cc),
                    bcc: trimEmails(this.bcc),
                };
                await API.post(`${ADMIN_DAMAGE_CLAIMS_EMAIL_ENDPOINT()}send/`, body);
                this.$emit('emailSent');
                this.successToast({ text: 'Success' });
                this.$bvModal.hide(this.modalId);
            } catch (e) {
                const { generic, fields } = formatResponseError(e);

                if (generic) {
                    this.errorToast({ text: generic });
                } else {
                    this.errorToast({ text: 'Could not send email' });
                }
            }
        },
        toggleCC(status) {
            this.isCC = status;
        },
        toggleBCC(status) {
            this.isBCC = status;
        },
        setInvalid(isInvalid) {
            this.invalid = isInvalid;
        },
        clearMessage() {
            this.message = this.userEmailSignature || '';
        },
    },
    watch: {
        initialValues() {
            this.claimData = this.initialValues;
        },
    },
};
</script>

<style lang="scss" scoped>
:deep(.modal-dialog) {
    max-width: 650px !important;
}

.cc-email-input {
    margin-left: 5px;
}

.apply-response-button {
    padding-bottom: 0.45rem !important;
    width: 100%;
    height: 2.75rem;
}
</style>
